import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { haptic } from '../utils/vibration';
import FloatingField, { normalizeFloatingLabel } from './FloatingField';
import ModalShell from './ModalShell';

/**
 * DatePicker - Универсальный компонент календаря
 * 
 * @param {string} value - Выбранная дата в формате YYYY-MM-DD
 * @param {function} onChange - Callback при выборе даты
 * @param {string} placeholder - Плейсхолдер для поля ввода
 * @param {boolean} disabled - Отключить календарь
 * @param {boolean} allowPastDates - Разрешить выбор прошедших дат (по умолчанию false)
 * 
 * Примеры использования:
 * 
 * // Для заказов - прошедшие даты заблокированы
 * <DatePicker
 *   value={orderDate}
 *   onChange={setOrderDate}
 *   allowPastDates={false}
 * />
 * 
 * // Для истории/отчетов - прошедшие даты доступны
 * <DatePicker
 *   value={reportDate}
 *   onChange={setReportDate}
 *   allowPastDates={true}
 * />
 * 
 * // Для фильтров - все даты доступны
 * <DatePicker
 *   value={filterDate}
 *   onChange={setFilterDate}
 *   allowPastDates={true}
 * />
 */
const DatePicker = ({
  value,
  onChange,
  label,
  placeholder = "Выберите дату",
  disabled = false,
  allowPastDates = false,
  showTodayButton = true,
  minDate = null,
  inputClassName = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [showYearPicker, setShowYearPicker] = useState(false);

  // Функция для безопасного создания даты из строки
  const parseDate = (dateString) => {
    if (!dateString) return null;
    // Создаем дату в локальном времени, избегая проблем с часовыми поясами
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  // Функция для форматирования даты в строку
  const formatDateToString = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getMinBoundaryDate = () => {
    if (allowPastDates && !minDate) {
      return null;
    }

    const fallback = new Date();
    fallback.setHours(0, 0, 0, 0);

    if (!minDate) {
      return fallback;
    }

    const parsedMinDate = typeof minDate === 'string' ? parseDate(minDate) : new Date(minDate);
    if (!parsedMinDate || Number.isNaN(parsedMinDate.getTime())) {
      return fallback;
    }

    parsedMinDate.setHours(0, 0, 0, 0);
    return parsedMinDate;
  };

  // Инициализация при изменении value
  useEffect(() => {
    if (value) {
      const parsedDate = parseDate(value);
      setSelectedDate(parsedDate);
      // Устанавливаем currentDate на месяц выбранной даты
      if (parsedDate) {
        setCurrentDate(new Date(parsedDate.getFullYear(), parsedDate.getMonth(), 1));
      }
    } else {
      setSelectedDate(null);
    }
  }, [value]);

  const daysOfWeek = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
  const months = [
    'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
    'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
  ];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const firstDayOfWeek = firstDay.getDay() || 7; // Понедельник = 1

    const days = [];
    
    // Получаем сегодняшнюю дату для сравнения
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Добавляем дни предыдущего месяца
    const prevMonth = new Date(year, month, 0);
    const daysInPrevMonth = prevMonth.getDate();
    const minDateObj = getMinBoundaryDate();

    for (let i = firstDayOfWeek - 1; i > 0; i--) {
      const date = new Date(year, month - 1, daysInPrevMonth - i + 1);
      const dateWithoutTime = new Date(date.getFullYear(), date.getMonth(), date.getDate());

      days.push({
        date,
        isCurrentMonth: false,
        isToday: dateWithoutTime.getTime() === today.getTime(),
        isSelected: false,
        isPast: !!minDateObj && dateWithoutTime < minDateObj
      });
    }

    // Добавляем дни текущего месяца
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(year, month, i);
      const dateWithoutTime = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const selectedWithoutTime = selectedDate ? new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate()) : null;

      days.push({
        date,
        isCurrentMonth: true,
        isToday: dateWithoutTime.getTime() === today.getTime(),
        isSelected: selectedWithoutTime && dateWithoutTime.getTime() === selectedWithoutTime.getTime(),
        isPast: !!minDateObj && dateWithoutTime < minDateObj
      });
    }

    // Добавляем дни следующего месяца
    const remainingDays = 42 - days.length; // 6 недель * 7 дней
    for (let i = 1; i <= remainingDays; i++) {
      const date = new Date(year, month + 1, i);
      const dateWithoutTime = new Date(date.getFullYear(), date.getMonth(), date.getDate());

      days.push({
        date,
        isCurrentMonth: false,
        isToday: false,
        isSelected: false,
        isPast: !!minDateObj && dateWithoutTime < minDateObj
      });
    }

    return days;
  };

  const handleDateSelect = (day) => {
    if (day.isCurrentMonth && (allowPastDates || !day.isPast)) {
      haptic('tap');
      setSelectedDate(day.date);
      onChange(formatDateToString(day.date));
      setIsOpen(false);
    }
  };

  const goToPreviousMonth = () => {
    haptic('tap');
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    haptic('tap');
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    haptic('tap');
    const today = new Date();
    setCurrentDate(new Date(today.getFullYear(), today.getMonth(), 1));
    setSelectedDate(today);
    onChange(formatDateToString(today));
    setIsOpen(false);
  };

  const selectMonth = (monthIndex) => {
    haptic('tap');
    setCurrentDate(new Date(currentDate.getFullYear(), monthIndex, 1));
    setShowMonthPicker(false);
  };

  const selectYear = (year) => {
    haptic('tap');
    setCurrentDate(new Date(year, currentDate.getMonth(), 1));
    setShowYearPicker(false);
  };

  const formatDisplayDate = (date) => {
    if (!date) return '';
    const days = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];
    return `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth()+1).toString().padStart(2, '0')} (${days[date.getDay()]})`;
  };

  const days = getDaysInMonth(currentDate);
  const currentYear = currentDate.getFullYear();
  const years = Array.from({ length: 21 }, (_, i) => currentYear - 10 + i);

  return (
    <div className="relative">
      {/* Поле ввода */}
      <FloatingField
        label={label || normalizeFloatingLabel(placeholder, 'Дата')}
        labelOffset="1.5rem"
        filled={!!selectedDate}
        active={isOpen}
        disabled={disabled}
      >
        <div className="relative">
          <input
            type="text"
            value={formatDisplayDate(selectedDate)}
            onChange={() => {}} // Read-only
            onClick={() => {
              if (!disabled) {
                haptic('open');
                setIsOpen(true);
              }
            }}
            onFocus={(e) => e.target.blur()}
            inputMode="none"
            tabIndex={-1}
            className={`w-full px-2 py-2 border rounded-xl text-[10px] tabular-nums tracking-[-0.01em] placeholder:text-[11px] placeholder:text-gray-400 focus:outline-none focus:ring-2 transition-colors dark:bg-gray-900 dark:text-gray-100 appearance-none cursor-pointer ${
              selectedDate
                ? 'border-green-500 focus:ring-green-500'
                : 'border-gray-300 dark:border-gray-700 focus:ring-blue-500 dark:focus:ring-blue-500'
            } ${selectedDate ? 'text-center pl-7 pr-7' : 'text-left pl-6 pr-7'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${inputClassName}`}
            placeholder={placeholder}
            readOnly
          />
          <CalendarIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3 h-3 text-blue-500 pointer-events-none" />
          {selectedDate && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                haptic('close');
                setSelectedDate(null);
                onChange('');
              }}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      </FloatingField>

      {/* Модальный календарь */}
      <ModalShell
        open={isOpen}
        onClose={() => setIsOpen(false)}
        variant="window"
        zIndexClass="z-[9999]"
        panelClassName="w-full max-w-xs rounded-2xl bg-white p-4 shadow-2xl dark:bg-gray-900"
      >
        {/* Заголовок модального окна */}
                  <div className="mb-4 flex items-center justify-between">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                      Выберите дату
                    </h3>
                    <button
                      onClick={() => {
                        haptic('close');
                        setIsOpen(false);
                      }}
                      className="rounded-lg p-1.5 transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
                    >
                      <X className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                    </button>
                  </div>

                {/* Навигация по месяцам */}
                <div className="mb-4 flex items-center justify-between">
                  <button
                    onClick={goToPreviousMonth}
                    className="rounded-lg p-1.5 transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
                  >
                    <ChevronLeft className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                  </button>

                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => {
                        haptic('tap');
                        setShowMonthPicker(!showMonthPicker);
                      }}
                      className="font-semibold text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400 transition-colors text-sm"
                    >
                      {months[currentDate.getMonth()]}
                    </button>
                    <button
                      onClick={() => {
                        haptic('tap');
                        setShowYearPicker(!showYearPicker);
                      }}
                      className="text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors text-sm"
                    >
                      {currentDate.getFullYear()}
                    </button>
                  </div>

                  <button
                    onClick={goToNextMonth}
                    className="rounded-lg p-1.5 transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
                  >
                    <ChevronRight className="h-4 w-4 text-gray-600 dark:text-gray-300" />
                  </button>
                </div>

                {/* Основное содержимое - календарь или селекторы */}
                <AnimatePresence mode="wait">
                  {showMonthPicker ? (
                    <motion.div
                      key="month-picker"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.2 }}
                      className="mb-6"
                    >
                      <div className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">Выберите месяц</div>
                      <div className="grid grid-cols-3 gap-2">
                        {months.map((month, index) => (
                          <motion.button
                            key={month}
                            onClick={() => selectMonth(index)}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className={`py-3 px-2 text-sm rounded-lg transition-colors ${
                              index === currentDate.getMonth()
                                ? 'bg-blue-500 text-white'
                                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                            }`}
                          >
                            {month}
                          </motion.button>
                        ))}
                      </div>
                    </motion.div>
                  ) : showYearPicker ? (
                    <motion.div
                      key="year-picker"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.2 }}
                      className="mb-6"
                    >
                      <div className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">Выберите год</div>
                      <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                        {years.map((year) => (
                          <motion.button
                            key={year}
                            onClick={() => selectYear(year)}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className={`py-3 px-2 text-sm rounded-lg transition-colors ${
                              year === currentDate.getFullYear()
                                ? 'bg-blue-500 text-white'
                                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                            }`}
                          >
                            {year}
                          </motion.button>
                        ))}
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="calendar"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.2 }}
                    >
                      {/* Дни недели */}
                      <div className="mb-4 grid grid-cols-7 gap-0.5">
                        {daysOfWeek.map((day, index) => (
                          <div
                            key={day}
                            className="h-8 flex items-center justify-center text-xs font-medium text-gray-500 dark:text-gray-400"
                          >
                            {day}
                          </div>
                        ))}
                      </div>

                      {/* Дни месяца */}
                      <div className="mb-4 grid grid-cols-7 gap-0.5">
                        {days.map((day, index) => (
                          <motion.button
                            key={index}
                            onClick={() => handleDateSelect(day)}
                            whileHover={!day.isPast ? { scale: 1.05 } : {}}
                            whileTap={!day.isPast ? { scale: 0.95 } : {}}
                            disabled={day.isPast && !allowPastDates}
                            className={`h-8 w-8 rounded-lg text-xs font-medium transition-all flex items-center justify-center ${
                              day.isSelected
                                ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg'
                                : day.isToday
                                ? 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 font-bold'
                                : day.isPast
                                ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed opacity-50'
                                : day.isCurrentMonth
                                ? 'text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800'
                                : 'text-gray-400 dark:text-gray-600'
                            }`}
                          >
                            {day.date.getDate()}
                          </motion.button>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Кнопки действий */}
                {showTodayButton && (
                  <div className="flex space-x-3">
                    <button
                      onClick={goToToday}
                      className="flex-1 rounded-xl border border-blue-200 px-4 py-3 text-sm font-medium text-blue-600 transition-colors hover:bg-blue-50 dark:border-blue-800 dark:text-blue-400 dark:hover:bg-blue-900/20"
                    >
                      Сегодня
                    </button>
                    <button
                      onClick={() => {
                        haptic('close');
                        setIsOpen(false);
                      }}
                      className="flex-1 rounded-xl border border-gray-200 px-4 py-3 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800"
                    >
                      Отмена
                    </button>
                  </div>
                )}
      </ModalShell>
    </div>
  );
};

export default DatePicker; 
